<template>
  <div class="price-management-page">
    <div class="filter-bar">
      <div class="filter-fields">
        <div class="filter-item filter-supplier">
          <span>供应商</span>
          <el-select v-model="filters.materialSupplierCode" filterable remote clearable :remote-method="searchFilterSuppliers" :loading="filterSupplierLoading" placeholder="请输入供应商名称或编码" @change="handleFilterSupplierChange" @visible-change="handleFilterSupplierVisible">
            <el-option v-for="item in filterSupplierOptions" :key="item.code" :label="`${item.name}（${item.code}）`" :value="item.code" />
            <el-option v-if="!filterSupplierLoading && !filterSupplierOptions.length" key="supplier-empty" label="暂无匹配的供应商" value="__empty__" disabled />
          </el-select>
        </div>
        <div class="filter-item filter-material">
          <span>物料</span>
          <el-select v-model="filters.materialCode" filterable clearable :disabled="!filters.materialSupplierCode" :loading="filterMaterialLoading" placeholder="请先选择供应商">
            <el-option v-for="item in filterMaterialOptions" :key="item.code" :label="item.description || item.code" :value="item.code" />
          </el-select>
        </div>
        <div class="filter-item">
          <span>物料分类</span>
          <el-select v-model="filters.materialCategoryFullName" clearable placeholder="全部">
            <el-option label="全部" value="" />
            <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
          </el-select>
        </div>
        <div class="filter-item filter-time">
          <span>变动时间</span>
          <el-date-picker v-model="filters.timeRange" type="daterange" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" value-format="yyyy-MM-dd" />
        </div>
      </div>
      <div class="filter-actions">
        <el-button @click="resetFilters">重置</el-button>
        <el-button type="primary" icon="el-icon-search" @click="applyFilters">搜索</el-button>
        <el-button type="primary" icon="el-icon-plus" class="add-button" @click="openCreate">新增涨价</el-button>
      </div>
    </div>

    <div v-loading="listLoading" class="list-area">
      <div v-if="filteredItems.length" class="price-grid">
        <article v-for="item in filteredItems" :key="item.id" class="price-card">
          <div class="card-heading">
            <h3>{{ item.name }}</h3>
            <div class="card-enable-control">
              <i v-if="isEnabling(item.id)" class="el-icon-loading"></i>
              <el-switch
                :value="item.enabled"
                :disabled="item.enabled || isEnabling(item.id)"
                active-color="#4a5ff3"
                @change="handleEnable(item)"
              />
            </div>
          </div>
          <div class="amount">¥{{ formatAmount(item.amount) }}</div>
          <div class="meta-row">
            <span><i class="el-icon-price-tag"></i>{{ item.code }}</span>
            <span><i class="el-icon-folder"></i>{{ item.category }}</span>
            <span><i class="el-icon-box"></i>{{ item.model }}</span>
            <span><i class="el-icon-date"></i>{{ item.period }}</span>
          </div>
          <p class="reason"><i class="el-icon-info"></i>{{ item.supplierName || '暂无供应商信息' }}</p>
          <div class="card-footer">
            <span class="status-tag" :class="`status-${item.status}`">{{ statusLabel(item.status) }}</span>
            <div class="card-actions">
              <button type="button" @click="openDetail(item)"><i class="el-icon-view"></i>详情</button>
              <button type="button" class="danger" @click="removeItem(item)"><i class="el-icon-delete"></i>删除</button>
            </div>
          </div>
        </article>
      </div>
      <el-empty v-else description="暂无符合条件的涨价信息" />
    </div>

    <div class="pagination-bar">
      <span>共{{ filteredItems.length }}条</span>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :total="filteredItems.length"
        :page-sizes="[12, 24, 36]"
        :page-size="12"
      />
    </div>

    <el-dialog
      title="新增涨价信息"
      :visible.sync="createVisible"
      width="720px"
      top="5vh"
      custom-class="price-create-dialog"
      append-to-body
      :close-on-click-modal="false"
      @closed="resetCreateForm"
    >
      <el-form ref="createForm" :model="createForm" :rules="formRules" label-position="top">
        <el-form-item label="供应商" prop="materialSupplierCode">
          <el-select
            v-model="createForm.materialSupplierCode"
            filterable
            remote
            :remote-method="searchCreateSuppliers"
            :loading="createSupplierLoading"
            placeholder="请输入供应商名称或编码搜索"
            @change="handleSupplierChange"
            @visible-change="handleCreateSupplierVisible"
          >
            <el-option
              v-for="item in createSupplierOptions"
              :key="item.code"
              :label="`${item.name}（${item.code}）`"
              :value="item.code"
            />
            <el-option v-if="!createSupplierLoading && !createSupplierOptions.length" key="supplier-empty" label="暂无匹配的供应商" value="__empty__" disabled />
          </el-select>
        </el-form-item>
        <el-form-item label="物料" prop="materialCode">
          <el-select
            v-model="createForm.materialCode"
            filterable
            :disabled="!createForm.materialSupplierCode"
            :loading="createMaterialLoading"
            placeholder="请先选择供应商，再搜索物料编码或描述"
            @change="handleMaterialChange"
          >
            <el-option
              v-for="item in createMaterialOptions"
              :key="item.code"
              :label="item.description || item.code"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="物料描述">
          <el-input v-model.trim="createForm.materialDescription" placeholder="请输入物料描述" />
        </el-form-item>
        <el-form-item label="物料分类" prop="materialCategoryFullName">
          <el-select v-model="createForm.materialCategoryFullName" placeholder="请选择">
            <el-option v-for="item in categories" :key="item" :label="item" :value="item" />
          </el-select>
        </el-form-item>
        <el-form-item label="原厂型号">
          <el-input v-model.trim="createForm.originalModel" placeholder="请输入原厂型号" />
        </el-form-item>
        <el-form-item label="调整金额（元）" prop="amount">
          <el-input v-model.trim="createForm.amount" placeholder="请输入调整金额" />
        </el-form-item>
        <el-form-item label="调价周期">
          <el-date-picker
            v-model="createForm.priceAdjustmentDateRange"
            type="daterange"
            format="yyyy年MM月dd日"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            clearable
          />
        </el-form-item>
        <el-form-item label="启用状态">
          <div class="enabled-field">
          <el-switch v-model="createForm.enabledStatus" :active-value="1" :inactive-value="0" active-color="#4a5ff3" />
            <span>{{ createForm.enabledStatus === 1 ? '已启用' : '未启用' }}</span>
          </div>
        </el-form-item>
      </el-form>
      <template slot="footer">
        <el-button @click="createVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitCreate">确定</el-button>
      </template>
    </el-dialog>

    <el-dialog
      :visible.sync="detailVisible"
      width="920px"
      top="0"
      custom-class="price-detail-dialog"
      append-to-body
      :show-close="false"
    >
      <template slot="title">
        <button class="back-button" type="button" @click="detailVisible = false">
          <i class="el-icon-back"></i>返回列表
        </button>
      </template>
      <div v-loading="detailLoading">
      <template v-if="currentItem">
        <div class="detail-title">
          <h2>{{ currentItem.name }}</h2>
          <span class="enabled-tag">{{ currentItem.enabled ? '已启用' : '未启用' }}</span>
        </div>
        <div class="detail-table">
          <div><span>物料号</span><strong>{{ currentItem.code }}</strong></div>
          <div><span>物料分类</span><strong>{{ currentItem.category }}</strong></div>
          <div><span>原厂型号</span><strong>{{ currentItem.model }}</strong></div>
          <div><span>供应商编码</span><strong>{{ currentItem.supplierCode || '-' }}</strong></div>
          <div><span>供应商名称</span><strong>{{ currentItem.supplierName || '-' }}</strong></div>
          <div><span>调整金额</span><strong class="detail-amount">¥{{ formatAmount(currentItem.amount) }}</strong></div>
          <div><span>调价周期</span><strong>{{ currentItem.period }}</strong></div>
          <div><span>调价类型</span><strong>{{ currentItem.type === 2 ? '降价' : '涨价' }}</strong></div>
          <div><span>AI状态</span><strong><em class="status-tag" :class="`status-${currentItem.status}`">{{ statusLabel(currentItem.status) }}</em></strong></div>
          <div><span>创建时间</span><strong>{{ currentItem.createdAt }}</strong></div>
        </div>
        <section v-if="currentItem.reportContent" class="analysis-report">
          <h3><i class="el-icon-document"></i>AI 智能分析报告</h3>
          <div
            v-for="(section, index) in parseReportSections(currentItem.reportContent)"
            :key="`${section.title}-${index}`"
            class="report-card"
          >
           <h4><span class="report-emoji">{{ section.emoji }}</span>{{ section.title }}</h4>
            <div class="report-markdown" v-html="renderMd(section.content)"></div>
          </div>
        </section>
        <section v-else-if="currentItem.isMock" class="analysis-report">
          <h3><i class="el-icon-document"></i>AI 智能分析报告</h3>
          <div class="report-card">
            <h4>📌 影响范围</h4>
            <p>该物料涨价预计影响 <b>3条产品线</b>（冰箱、空调、洗衣机），涉及 <b>2家核心客户</b>（海尔、创维）。</p>
          </div>
          <div class="report-card">
            <h4>⚠️ 风险等级：<span class="risk-tag">中等风险</span></h4>
            <p>该涨价信息对供应链的整体影响程度为 <b>中等</b>，建议优先关注并制定应对策略。</p>
          </div>
          <div class="report-card">
            <h4>✅ 建议措施</h4>
            <ol>
              <li>启动备选供应商认证，降低单源依赖风险</li>
              <li>与客户沟通价格传导方案，协商分担成本</li>
              <li>增加安全库存至45天，覆盖交期波动</li>
            </ol>
          </div>
          <div class="report-card">
            <h4>💡 决策建议</h4>
            <p>建议在 <b>一周内</b> 完成供应商议价并制定分级应对方案。</p>
          </div>
        </section>
      </template>
      </div>
      <template v-if="currentItem && currentItem.status === 'failed'" slot="footer">
        <el-button type="primary" icon="el-icon-refresh" :loading="reparseLoading" @click="handleReparse">重新解析</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  addMaterialPriceChange,
  analyzeAndCreateMaterialPriceDecision,
  deleteMaterialPriceChange,
  getMaterialPriceChangeDetail,
  queryLakeMaterialsBySupplier,
  queryLakeSuppliers,
  queryMaterialPriceChanges,
  updateMaterialPriceChangeStatus
} from '@/api/supply-chain'
import { renderMarkdownText } from '@/utils/markdownRenderer'
import mermaid from 'mermaid'

const EMPTY_FORM = () => ({
  materialCode: '',
  materialName: '',
  materialDescription: '',
  originalModel: '',
  materialCategoryFullName: '',
  materialSupplierCode: '',
  materialSupplierName: '',
  type: 1,
  amount: '',
  priceAdjustmentCycle: '',
  priceAdjustmentDateRange: [],
  enabledStatus: 1,
  agentId: '',
  workspaceId: ''
})

export default {
  name: 'PriceIncreaseManagement',
  components: {},
  data() {
    const positiveAmount = (rule, value, callback) => {
      const amount = Number(String(value).replace(/,/g, ''))
      if (!Number.isFinite(amount) || amount <= 0) callback(new Error('请输入大于0的数字'))
      else callback()
    }
    return {
      categories: ['电感', '塑料件', '显示罩', '显示模组', '继电器', 'PCB', '物料类', '散热器', '焊料', '线束'],
      statusOptions: [
        { label: '待解析', value: 'pending' },
        { label: '解析中', value: 'processing' },
        { label: '解析成功', value: 'completed' },
        { label: '解析失败', value: 'failed' }
      ],
      filters: {
        materialCode: '',
        materialCategoryFullName: '',
        materialSupplierCode: '',
        materialSupplierName: '',
        timeRange: []
      },
      items: [],
      listLoading: false,
      createVisible: false,
      detailVisible: false,
      currentItem: null,
      createForm: EMPTY_FORM(),
      submitting: false,
      detailLoading: false,
      reparseLoading: false,
      enablingIds: {},
      filterSupplierLoading: false,
      filterMaterialLoading: false,
      createSupplierLoading: false,
      createMaterialLoading: false,
      filterSupplierRequestId: 0,
      createSupplierRequestId: 0,
      filterSupplierOptions: [],
      filterMaterialOptions: [],
      createSupplierOptions: [],
      createMaterialOptions: [],
      formRules: {
        materialCode: [{ required: true, message: '请选择物料', trigger: 'change' }],
        materialCategoryFullName: [{ required: true, message: '请选择物料分类', trigger: 'change' }],
        materialSupplierCode: [{ required: true, message: '请选择供应商', trigger: 'change' }],
        amount: [{ required: true, validator: positiveAmount, trigger: 'blur' }]
      }
    }
  },
  computed: {
    filteredItems() {
      return this.items
    }
  },
  created() {
    this.fetchList()
    this.prefetchSuppliers()
  },
  methods: {
    renderMd(content) {
      if (!content) return ''
      const html = renderMarkdownText(content)
      this.$nextTick(() => this._initMermaidInDetail())
      return html
    },
    _initMermaidInDetail() {
      if (typeof document === 'undefined') return
      const blocks = document.querySelectorAll('.report-markdown .mermaid-block__body')
      blocks.forEach(async (body) => {
        const block = body.closest('.mermaid-block')
        if (!block || body.innerHTML) return
        const encoded = block.getAttribute('data-mermaid')
        if (!encoded) return
        try {
          const code = decodeURIComponent(encoded)
          const { svg } = await mermaid.render('mm-pr-' + Math.random().toString(36).slice(2), code)
          body.innerHTML = svg
        } catch (e) {
          body.innerHTML = '<div class="mermaid-error">图表渲染失败</div>'
        }
      })
    },
    statusLabel(status) {
      const found = this.statusOptions.find((item) => item.value === status)
      return found ? found.label : status
    },
    formatAmount(value) {
      return Number(value || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    isEnabling(id) {
      return Boolean(this.enablingIds[id])
    },
    async handleEnable(item) {
      if (!item || !item.id || item.enabled || this.isEnabling(item.id)) return
      this.$set(this.enablingIds, item.id, true)
      try {
        await updateMaterialPriceChangeStatus({ id: item.id, status: 1 })
        this.$set(item, 'enabled', true)
        try {
          await analyzeAndCreateMaterialPriceDecision(item.id)
          this.$message.success('启用成功，解析任务已提交')
        } catch (error) {
          this.$message.warning((error && (error.msg || error.message)) || '已启用，但解析任务提交失败，请稍后重新解析')
        }
        await this.fetchList()
      } catch (error) {
        this.$message.error((error && (error.msg || error.message)) || '启用失败')
      } finally {
        this.$delete(this.enablingIds, item.id)
      }
    },
    applyFilters() {
      this.fetchList()
    },
    resetFilters() {
      this.filters = {
        materialCode: '',
        materialCategoryFullName: '',
        materialSupplierCode: '',
        materialSupplierName: '',
        timeRange: []
      }
      this.filterMaterialOptions = []
      this.fetchList()
    },
    openCreate() {
      this.createForm.agentId = this.getAgentIdFromUrl()
      this.createForm.workspaceId = localStorage.getItem('claw_workspace_id') || ''
      this.createVisible = true
    },
    getAgentIdFromUrl() {
      const routeAgentId = this.$route && this.$route.query && this.$route.query.agentId
      if (routeAgentId) return routeAgentId
      const params = new URLSearchParams(window.location.search)
      return params.get('agentId') || ''
    },
    resetCreateForm() {
      this.createForm = EMPTY_FORM()
      this.createMaterialOptions = []
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.clearValidate())
    },
    submitCreate() {
      this.$refs.createForm.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const { priceAdjustmentDateRange, ...formData } = this.createForm
          const [priceAdjustmentStartDate, priceAdjustmentEndDate] = priceAdjustmentDateRange || []
          await addMaterialPriceChange({
            ...formData,
            priceAdjustmentStartDate,
            priceAdjustmentEndDate,
            amount: Number(String(this.createForm.amount).replace(/,/g, ''))
          })
          this.createVisible = false
          this.$message.success('涨价信息新增成功')
          await this.fetchList()
        } catch (error) {
          this.$message.error((error && (error.msg || error.message)) || '新增失败')
        } finally {
          this.submitting = false
        }
      })
    },
    async openDetail(item) {
      this.detailVisible = true
      this.currentItem = item
      if (!item.id) return
      this.detailLoading = true
      try {
        const res = await getMaterialPriceChangeDetail(item.id)
        this.currentItem = this.mapApiItem(res.data)
      } catch (error) {
        this.detailVisible = false
        this.$message.error((error && (error.msg || error.message)) || '详情获取失败')
      } finally {
        this.detailLoading = false
      }
    },
    removeItem(item) {
      this.$confirm(`确认删除“${item.name}”吗？`, '删除确认', {
        confirmButtonText: '删除', cancelButtonText: '取消', type: 'warning'
      }).then(async () => {
        try {
          await deleteMaterialPriceChange(item.id)
          await this.fetchList()
          this.$message.success('删除成功')
        } catch (error) {
          this.$message.error((error && (error.msg || error.message)) || '删除失败')
        }
      }).catch(() => {})
    },
    mapApiItem(item) {
      const statusMap = { 0: 'pending', 1: 'completed', 2: 'processing', 3: 'failed', 4: 'completed', 5: 'completed' }
      return {
        id: item.id,
        name: item.materialName || item.materialCode,
        code: item.materialCode,
        category: item.materialCategoryFullName,
        model: item.originalModel || '-',
        supplierCode: item.materialSupplierCode,
        supplierName: item.materialSupplierName,
        type: item.type,
        amount: item.amount,
        period: this.formatAdjustmentPeriod(item),
        status: statusMap[item.parseStatus] || 'pending',
        enabled: item.enabledStatus === 1,
        reportContent: item.reportContent,
        createdAt: item.createTime || '-'
      }
    },
    async handleReparse() {
      if (!this.currentItem || !this.currentItem.id || this.reparseLoading) return
      this.reparseLoading = true
      try {
        await analyzeAndCreateMaterialPriceDecision(this.currentItem.id)
        this.detailVisible = false
        this.$message.success('重新解析任务已提交')
        await this.fetchList()
      } catch (error) {
        this.$message.error((error && (error.msg || error.message)) || '重新解析任务提交失败')
      } finally {
        this.reparseLoading = false
      }
    },
    formatAdjustmentPeriod(item) {
      const startDate = item.priceAdjustmentStartDate
      const endDate = item.priceAdjustmentEndDate
      if (startDate && endDate) return `${startDate} 至 ${endDate}`
      return startDate || endDate || item.priceAdjustmentCycle || '-'
    },
    parseReportSections(content) {
      const lines = String(content || '').split(/\r?\n/)
      const sections = []
      const preface = []
      let currentSection = null

      const appendCurrent = () => {
        if (!currentSection) return
        sections.push({
          title: currentSection.title,
          content: currentSection.lines.join('\n').trim()
        })
      }

      lines.forEach((line) => {
        const secondLevelHeading = line.match(/^##\s+(.+?)\s*$/)
        if (secondLevelHeading) {
          appendCurrent()
          currentSection = { title: secondLevelHeading[1], lines: [] }
          return
        }
        if (/^#\s+/.test(line)) return
        if (currentSection) currentSection.lines.push(line)
        else if (line.trim()) preface.push(line)
      })
      appendCurrent()

      const result = sections.length
        ? sections
        : [{ title: '分析详情', content: preface.join('\n').trim() }]
      return result.filter((section) => section.content).map((section, index) => ({
        ...section,
        emoji: this.getReportSectionEmoji(section.title, index)
      }))
    },
    getReportSectionEmoji(title, index) {
      const emojiRules = [
        { pattern: /风险|预警|异常|问题/, emoji: '⚠️' },
        { pattern: /影响|范围/, emoji: '📌' },
        { pattern: /决策|结论/, emoji: '💡' },
        { pattern: /建议|措施|策略|行动/, emoji: '✅' },
        { pattern: /价格|调价|金额|成本/, emoji: '💰' },
        { pattern: /趋势|预测/, emoji: '📈' },
        { pattern: /供应商|供应链/, emoji: '🏭' },
        { pattern: /物料|产品/, emoji: '📦' },
        { pattern: /概览|摘要|背景/, emoji: '📊' }
      ]
      const matched = emojiRules.find((rule) => rule.pattern.test(title))
      const defaults = ['📄', '🔎', '📋', '🧭']
      return matched ? matched.emoji : defaults[index % defaults.length]
    },
    async fetchList() {
      this.listLoading = true
      try {
        const params = {
          materialCode: this.filters.materialCode || undefined,
          materialCategoryFullName: this.filters.materialCategoryFullName || undefined,
          materialSupplierCode: this.filters.materialSupplierCode || undefined,
          materialSupplierName: this.filters.materialSupplierName || undefined,
          startTime: this.filters.timeRange[0] || undefined,
          endTime: this.filters.timeRange[1] || undefined
        }
        const res = await queryMaterialPriceChanges(params)
        const data = res && res.data
        const records = Array.isArray(data)
          ? data
          : (data && (data.records || data.list || data.rows)) || []
        this.items = records.map(this.mapApiItem)
      } catch (error) {
        this.items = []
        this.$message.error((error && (error.msg || error.message)) || '列表查询失败')
      } finally {
        this.listLoading = false
      }
    },
    normalizeSuppliers(data) {
      return (Array.isArray(data) ? data : []).map((item) => ({
        code: item.materialSupplierCode,
        name: item.materialSupplierName
      })).filter((item) => item.code)
    },
    normalizeMaterials(data) {
      return (Array.isArray(data) ? data : []).map((item) => ({
        code: item.materialCode,
        description: item.materialDesc || ''
      })).filter((item) => item.code)
    },
    async prefetchSuppliers() {
      const filterRequestId = ++this.filterSupplierRequestId
      const createRequestId = ++this.createSupplierRequestId
      this.filterSupplierLoading = true
      this.createSupplierLoading = true
      try {
        const res = await queryLakeSuppliers({ materialSupplierName: '' })
        const options = this.normalizeSuppliers(res && res.data)
        if (filterRequestId === this.filterSupplierRequestId) this.filterSupplierOptions = options
        if (createRequestId === this.createSupplierRequestId) this.createSupplierOptions = options.slice()
      } catch (error) {
        if (filterRequestId === this.filterSupplierRequestId) this.filterSupplierOptions = []
        if (createRequestId === this.createSupplierRequestId) this.createSupplierOptions = []
        if (filterRequestId === this.filterSupplierRequestId || createRequestId === this.createSupplierRequestId) {
          this.$message.error((error && (error.msg || error.message)) || '供应商预加载失败')
        }
      } finally {
        if (filterRequestId === this.filterSupplierRequestId) this.filterSupplierLoading = false
        if (createRequestId === this.createSupplierRequestId) this.createSupplierLoading = false
      }
    },
    async loadSuppliers(keyword, target) {
      const loadingKey = target === 'create' ? 'createSupplierLoading' : 'filterSupplierLoading'
      const optionsKey = target === 'create' ? 'createSupplierOptions' : 'filterSupplierOptions'
      const requestKey = target === 'create' ? 'createSupplierRequestId' : 'filterSupplierRequestId'
      const requestId = ++this[requestKey]
      this[loadingKey] = true
      try {
        const res = await queryLakeSuppliers({ materialSupplierName: keyword || '' })
        if (requestId === this[requestKey]) this[optionsKey] = this.normalizeSuppliers(res && res.data)
      } catch (error) {
        if (requestId === this[requestKey]) {
          this[optionsKey] = []
          this.$message.error((error && (error.msg || error.message)) || '供应商查询失败')
        }
      } finally {
        if (requestId === this[requestKey]) this[loadingKey] = false
      }
    },
    searchFilterSuppliers(keyword) {
      this.loadSuppliers(keyword, 'filter')
    },
    searchCreateSuppliers(keyword) {
      this.loadSuppliers(keyword, 'create')
    },
    handleFilterSupplierVisible(visible) {
      if (visible && !this.filterSupplierOptions.length && !this.filterSupplierLoading) this.loadSuppliers('', 'filter')
    },
    handleCreateSupplierVisible(visible) {
      if (visible && !this.createSupplierOptions.length && !this.createSupplierLoading) this.loadSuppliers('', 'create')
    },
    async loadMaterials(supplierCode, target) {
      const loadingKey = target === 'create' ? 'createMaterialLoading' : 'filterMaterialLoading'
      const optionsKey = target === 'create' ? 'createMaterialOptions' : 'filterMaterialOptions'
      if (!supplierCode) {
        this[optionsKey] = []
        return
      }
      this[loadingKey] = true
      try {
        const res = await queryLakeMaterialsBySupplier({ supplierCode })
        this[optionsKey] = this.normalizeMaterials(res && res.data)
      } catch (error) {
        this[optionsKey] = []
        this.$message.error((error && (error.msg || error.message)) || '物料查询失败')
      } finally {
        this[loadingKey] = false
      }
    },
    handleMaterialChange(code) {
      const selected = this.createMaterialOptions.find((item) => item.code === code)
      this.createForm.materialName = selected ? selected.description : ''
    },
    handleSupplierChange(code) {
      const selected = this.createSupplierOptions.find((item) => item.code === code)
      this.createForm.materialSupplierName = selected ? selected.name : ''
      this.createForm.materialCode = ''
      this.createForm.materialName = ''
      this.createMaterialOptions = []
      this.$nextTick(() => this.$refs.createForm && this.$refs.createForm.clearValidate('materialCode'))
      this.loadMaterials(code, 'create')
    },
    handleFilterSupplierChange(code) {
      const selected = this.filterSupplierOptions.find((item) => item.code === code)
      this.filters.materialSupplierName = selected ? selected.name : ''
      this.filters.materialCode = ''
      this.filterMaterialOptions = []
      this.loadMaterials(code, 'filter')
    }
  }
}
</script>

<style lang="scss" scoped>
.price-management-page {
  --primary: #4a5ff3;
  --ink: #1e293b;
  --muted: #8b8f97;
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  background: #f3f5f9;
  color: var(--ink);
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}
.filter-bar {
  display: flex;
  padding: 18px 24px;
  border-radius: 6px;
  background: #fff;
  gap: 16px;
  align-items: flex-end;
  justify-content: space-between;
  flex-wrap: wrap;
  box-shadow: 0 1px 0 #e8ebf1;
}
.filter-fields, .filter-actions { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; }
.filter-fields { flex: 1; }
.filter-item { display: flex; align-items: center; gap: 8px; }
.filter-item > span { color: #74777d; font-size: 13px; white-space: nowrap; }
.filter-item ::v-deep .el-input { width: 150px; }
.filter-material ::v-deep .el-input,
.filter-supplier ::v-deep .el-input { width: 220px; }
.filter-time ::v-deep .el-date-editor { width: 360px; }
.filter-actions .add-button { margin-left: 8px; }
.list-area { flex: 1; min-height: 0; overflow-y: auto; padding: 18px 0; scrollbar-width: none; }
.list-area::-webkit-scrollbar { width: 0; height: 0; }
.price-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; }
.price-card {
  display: flex;
  min-width: 0;
  padding: 22px 24px 16px;
  border: 1px solid #e7e9ee;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 5px 20px rgba(30, 41, 59, 0.035);
  flex-direction: column;
}
.card-heading { display: flex; justify-content: space-between; align-items: center; gap: 16px; }
.card-heading h3 { margin: 0; overflow: hidden; font-size: 18px; white-space: nowrap; text-overflow: ellipsis; }
.card-enable-control { display: flex; flex: none; align-items: center; gap: 6px; color: #4a5ff3; }
.amount { margin: 14px 0; color: #ed4437; font-size: 24px; font-weight: 700; }
.meta-row { display: flex; flex-wrap: wrap; gap: 10px 18px; color: #85878c; font-size: 13px; }
.meta-row span { min-width: 0; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.meta-row i, .reason i { margin-right: 7px; color: #aaaeb5; }
.reason { margin: 14px 0 12px; color: #8c8f95; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.card-footer { display: flex; min-height: 38px; margin-top: auto; padding-top: 12px; border-top: 1px solid #edf0f4; align-items: center; justify-content: space-between; }
.status-tag { display: inline-flex; padding: 4px 9px; border-radius: 5px; font-size: 12px; font-style: normal; font-weight: 600; }
.status-completed { color: #0ca94e; background: #effaf3; }
.status-processing { color: #2d8cf0; background: #eaf5ff; }
.status-pending { color: #8b8f96; background: #f1f2f4; }
.status-failed { color: #ef312e; background: #fff0ef; }
.card-actions { display: flex; gap: 18px; }
.card-actions button { padding: 0; border: 0; background: none; color: var(--primary); cursor: pointer; font-size: 13px; }
.card-actions button.danger { color: #f34545; }
.card-actions i { margin-right: 5px; }
.pagination-bar { display: flex; padding: 14px 24px; background: #fff; color: #777b82; align-items: center; justify-content: space-between; }
.enabled-field { display: flex; align-items: center; gap: 10px; color: #7b7e84; }
@media (max-width: 1180px) {
  .price-grid { grid-template-columns: 1fr; }
  .filter-actions { width: 100%; justify-content: flex-end; }
}
@media (max-width: 720px) {
  .filter-bar { padding-left: 14px; padding-right: 14px; }
  .filter-item,
  .filter-item ::v-deep .el-input,
  .filter-material ::v-deep .el-input,
  .filter-supplier ::v-deep .el-input,
  .filter-time ::v-deep .el-date-editor { width: 100%; }
  .filter-item > span { width: 62px; }
  .price-card { padding: 18px; }
  .meta-row { flex-direction: column; }
}
</style>

<style lang="scss">
.price-create-dialog, .price-detail-dialog {
  border-radius: 18px;
  overflow: hidden;
  .el-dialog__header { padding: 22px 28px; border-bottom: 1px solid #edf0f4; }
  .el-dialog__title { color: #1e293b; font-size: 18px; font-weight: 700; }
}
.price-create-dialog {
  .el-dialog__body { max-height: 72vh; overflow-y: auto; padding: 24px 28px 6px; }
  .el-form-item { margin-bottom: 18px; }
  .el-form-item__label { padding: 0 0 7px; color: #263248; font-weight: 600; line-height: 20px; }
  .el-select { width: 100%; }
  .el-date-editor { width: 100%; }
  .el-dialog__footer { padding: 16px 28px 20px; border-top: 1px solid #edf0f4; }
}
.price-detail-dialog {
  position: absolute;
  top: 50%;
  left: 50%;
  margin: 0 !important;
  transform: translate(-50%, -50%);
  .el-dialog__header { padding: 18px 28px; }
  .el-dialog__body { max-height: calc(100vh - 190px); overflow-y: auto; padding: 24px 28px 28px; }
  .el-dialog__footer { padding: 16px 28px 20px; border-top: 1px solid #edf0f4; }
  .back-button { padding: 0; border: 0; background: none; color: #4a5ff3; font-size: 14px; font-weight: 600; cursor: pointer; }
  .back-button i { margin-right: 8px; }
  .detail-title { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
  .detail-title h2 { margin: 0; color: #1e293b; font-size: 22px; }
  .enabled-tag { padding: 4px 9px; border-radius: 5px; background: #effaf3; color: #0ca94e; font-size: 12px; font-weight: 600; }
  .detail-table > div { display: grid; grid-template-columns: 130px 1fr; min-height: 48px; border-bottom: 1px solid #edf0f4; align-items: center; }
  .detail-table span { color: #8b8f95; font-size: 14px; font-weight: 600; }
  .detail-table strong { color: #34383f; font-size: 14px; font-weight: 500; }
  .detail-table .detail-amount { color: #ed4437; font-size: 20px; font-weight: 700; }
  .analysis-report { margin-top: 22px; padding-top: 18px; border-top: 1px solid #e5e9ef; }
  .analysis-report > h3 { margin: 0 0 14px; color: #263248; font-size: 17px; }
  .analysis-report > h3 i { margin-right: 8px; }
  .report-card { margin-top: 12px; padding: 17px 20px; border-radius: 12px; background: #f7f9fc; }
  .report-card h4 { display: flex; margin: 0 0 8px; color: #334155; font-size: 14px; align-items: center; }
  .report-emoji { margin-right: 7px; font-size: 16px; }
  .report-card p, .report-card ol, .report-card ul { color: #6e737c; font-size: 13px; line-height: 1.8; }
  .report-card p { margin: 6px 0; }
  .report-card ol, .report-card ul { margin: 6px 0; padding-left: 0; list-style-position: inside; }
  .report-card .report-markdown > :first-child { margin-top: 0; }
  .report-card .report-markdown > :last-child { margin-bottom: 0; }
  .report-card .report-markdown h3 { margin: 12px 0 6px; color: #475569; font-size: 13px; }
  .report-card b { color: #4a5ff3; }
  .risk-tag { padding: 4px 8px; border-radius: 5px; background: #fff4e8; color: #f39718; font-size: 12px; }
}
@media (max-width: 760px) {
  .price-create-dialog, .price-detail-dialog { width: calc(100% - 24px) !important; }
}
</style>

<style lang="scss">
@import '@/assets/styles/report-markdown.scss';
</style>
